import {
  CashCheckVerificationMethod,
  ICashCheckVerification,
} from "../../models/order/cash-check";

export interface IWeightFinalizationCycle {
  finalization: ICashCheckVerification;
  scaleScans: ICashCheckVerification[];
  isLatestFinalization: boolean;
  hasNewerWeightEvent: boolean;
  /** Server ordering rule: latest finalization and no later weight event. */
  isUndoableByOrder: boolean;
}

export type WeightVerificationTimelineEntry =
  | { kind: "cycle"; cycle: IWeightFinalizationCycle }
  | { kind: "verification"; verification: ICashCheckVerification };

export interface IWeightVerificationTimeline {
  entries: WeightVerificationTimelineEntry[];
  latestPendingScaleScanId?: number;
  latestManualWeightId?: number;
  scaleScanCount: number;
}

const hasMethod = (
  verification: ICashCheckVerification,
  method: CashCheckVerificationMethod,
  methodName: string,
): boolean =>
  verification.method === method || verification.methodName === methodName;

export const isScaleScanVerification = (
  verification: ICashCheckVerification,
): boolean =>
  hasMethod(verification, CashCheckVerificationMethod.SCALE_SCAN, "SCALE_SCAN");

export const isWeightFinalizationVerification = (
  verification: ICashCheckVerification,
): boolean =>
  hasMethod(
    verification,
    CashCheckVerificationMethod.WEIGHT_FINALIZATION,
    "WEIGHT_FINALIZATION",
  );

const isManualWeightVerification = (
  verification: ICashCheckVerification,
): boolean =>
  hasMethod(
    verification,
    CashCheckVerificationMethod.MANUAL_WEIGHT,
    "MANUAL_WEIGHT",
  );

const isWeightEvent = (verification: ICashCheckVerification): boolean =>
  isScaleScanVerification(verification) ||
  isManualWeightVerification(verification) ||
  isWeightFinalizationVerification(verification);

const maximumId = (
  verifications: ICashCheckVerification[],
  predicate: (verification: ICashCheckVerification) => boolean,
): number | undefined =>
  verifications.reduce<number | undefined>(
    (latest, verification) =>
      predicate(verification) &&
      (latest === undefined || verification.id > latest)
        ? verification.id
        : latest,
    undefined,
  );

/**
 * Builds the audit timeline for one ProductOrder only. Linked SCALE_SCAN rows
 * become children of their WEIGHT_FINALIZATION instead of independent actions.
 */
export const analyzeWeightVerificationTimeline = (
  verifications: ICashCheckVerification[],
): IWeightVerificationTimeline => {
  const finalizations = verifications.filter(isWeightFinalizationVerification);
  const finalizationIds = new Set(
    finalizations.map((verification) => verification.id),
  );
  const latestFinalizationId = maximumId(
    verifications,
    isWeightFinalizationVerification,
  );
  const latestWeightEventId = maximumId(verifications, isWeightEvent);
  const linkedScansByFinalizationId = new Map<
    number,
    ICashCheckVerification[]
  >();

  verifications.forEach((verification) => {
    if (
      !isScaleScanVerification(verification) ||
      verification.finalizationVerificationId == null ||
      !finalizationIds.has(verification.finalizationVerificationId)
    ) {
      return;
    }

    const linked =
      linkedScansByFinalizationId.get(
        verification.finalizationVerificationId,
      ) ?? [];
    linked.push(verification);
    linkedScansByFinalizationId.set(
      verification.finalizationVerificationId,
      linked,
    );
  });

  const entries = verifications.flatMap<WeightVerificationTimelineEntry>(
    (verification) => {
      if (
        isScaleScanVerification(verification) &&
        verification.finalizationVerificationId != null &&
        finalizationIds.has(verification.finalizationVerificationId)
      ) {
        return [];
      }

      if (isWeightFinalizationVerification(verification)) {
        return [
          {
            kind: "cycle",
            cycle: {
              finalization: verification,
              scaleScans:
                linkedScansByFinalizationId.get(verification.id) ?? [],
              isLatestFinalization: verification.id === latestFinalizationId,
              hasNewerWeightEvent:
                latestWeightEventId !== undefined &&
                latestWeightEventId > verification.id,
              isUndoableByOrder:
                verification.id === latestFinalizationId &&
                verification.id === latestWeightEventId,
            },
          },
        ];
      }

      return [{ kind: "verification", verification }];
    },
  );

  return {
    entries,
    latestPendingScaleScanId: maximumId(
      verifications,
      (verification) =>
        isScaleScanVerification(verification) &&
        verification.finalizationVerificationId == null &&
        !verification.isSystemFinalization,
    ),
    latestManualWeightId: maximumId(
      verifications,
      (verification) =>
        isManualWeightVerification(verification) &&
        !verification.isSystemFinalization,
    ),
    scaleScanCount: verifications.filter(isScaleScanVerification).length,
  };
};
